import React, { useContext, useState, useEffect, FunctionComponent, useMemo } from "react";
import { useTranslation } from "react-i18next";

// MUI Core
import { CircularProgress } from "@mui/material";
// Custom Components
import ModelTable from "@/ui/Table/ModelTable";
import DialogContext from "@/ui/DialogContext/DialogContext";

import modelDijelovi from "@/models/dijelovi"
import modelObjekti from "@/models/objekti"
import dataController from "@/lib/dataController";
import LoaderContext from "@/components/LoaderContext/LoaderContext";
import OperationsContext from "@/components/OperationsContext/OperationsContext";
import UserContext from "@/components/UserContext/UserContext";
import SnackbarContext from "@/ui/SnackbarContext/SnackbarContext";
import { Permissions } from "@/lib/permissions";

//Types
import { IDataController, DCRecord } from "@/@types/lib/dataController"
import { LoaderContextType } from "@/@types/context/LoaderContext";
import { SnackbarContextType } from "@/@types/ui/SnackbarContext";
import { OperationsContextType } from "@/components/OperationsContext/OperationsContext";
import { DialogContextType } from "@/@types/ui/DialogContext";

// TODO:
// subModels cleanup: they are added to the project because Table.jsx can not function without them

interface IQualityControlDijeloviTableProps {
  isQcChecked: boolean;
  baseRecordPath: string;
  baseRecordId: number | null;
  selectedKat: DCRecord | null;

  onRowSelect: (id: number, record: DCRecord) => void;
  // onLoad?: (records: Array<DCRecord>) => void;
}

const FOR_CALC = [1, 2, 3, 5, 6, 7, 8]; // namjena_ids that need to be multiplied with koef_kn to get pre_pn

const QualityControlDijeloviTable: FunctionComponent<React.PropsWithChildren<IQualityControlDijeloviTableProps>> = (props) => {
  const dialogContext = useContext<DialogContextType>(DialogContext);
  const loaderContext = useContext(LoaderContext) as LoaderContextType;
  const userContext = useContext(UserContext);
  const operationsContext = useContext<OperationsContextType>(OperationsContext);
  const snackbarContext = useContext(SnackbarContext) as SnackbarContextType;
  const { t } = useTranslation();
  const {
    baseRecordPath,
    baseRecordId,
    selectedKat,
    onRowSelect,
    isQcChecked,
  } = props;

  const [records, setRecords] = useState<DCRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [currKatId, setCurrKatId] = useState(-1);
  const [revisionCounter, setRevisionCounter] = useState(0);
  const [pending, setPending] = useState<boolean>(false);
  const [idToDeselect, setIdToDeselect] = useState(-1);

  const endsWithSlash = baseRecordPath.lastIndexOf('/') === baseRecordPath.length - 1 ? true : false;

  const url = `${baseRecordPath}${endsWithSlash ? "" : "/"}${baseRecordId}/katovi/${currKatId}/dijelovi`
  const dc: IDataController = new dataController(modelDijelovi, url);

  const urlObjekti = `${baseRecordPath}`;
  const dcObjekti: IDataController = new dataController(modelObjekti, urlObjekti);

  useEffect(() => {
    setRecords([]);
  }, [baseRecordId])

  useEffect(() => {
    const katId = selectedKat?.id as number ?? -1;
    if (baseRecordId !== null && katId !== -1) {
      if (katId !== currKatId) {
        setCurrKatId(katId);
      }
    } else {
      setCurrKatId(-1);
    }
  }, [selectedKat]);

  useEffect(() => {
    setRecords([]);
    refreshRecords();
  }, [currKatId])

  useEffect(() => {
    refreshRecords();
  }, [revisionCounter])

  const refreshRecords = () => {
    if (baseRecordId !== null && currKatId !== -1) {
      setLoading(true);
      dc.GetData()
        .then((resp) => {
          if (resp.success && Array.isArray(resp.data)) {
            const recs = resp.data.map((x) => {
              if (x.namjena_id && FOR_CALC.includes(Number(x.namjena_id))) {
                return { ...x, pre_pn: (Number(x.pre_pb) * Number(operationsContext.selectedProject?.koef_kn)).toFixed(2) }
              } else {
                return { ...x, pre_pn: x.pre_pb };
              }
            })
            setRecords(recs);
          };
        })
        .catch((resp) => {
          console.log(resp);
        })
        .finally(() => {
          setLoading(false)
        });
    }
  };

  const handleRowSelect = (id: number) => {
    //setEditRecordId(prevId => id === prevId ? -1 : id);
  }

  const katNumber = useMemo(() => {
    if (selectedKat) {
      return selectedKat.broj_kata as number;
    } else return null;
  }, [selectedKat])


  const isAdmin = userContext?.hasAnyPermission([Permissions.Administration]) ? true : false;

  return (
    <ModelTable
      title={t("titles.dijelovi")}
      subTitle={<>
        {katNumber !== null ? <span style={{ fontSize: "12px" }}>({t("titles.kat")}: {katNumber})</span> : null}
        {loading ?
          <CircularProgress disableShrink size={20} />
          : null}
      </>}
      records={records}
      viewName={isAdmin ? "admin" : "default"}
      dc={dc}
      allowSearchBar={false}
      allowSelection="one"
      allowFilter={false}
      allowExport={false}
      allowAdd={false}
      allowEdit={false}
      allowInlineEdit={false}
      allowInlineEditQuickly={false}
      allowDelete={false}
      onDeleted={() => { } }
      addLabel={t("buttons.new_m")}
      addTooltip="tooltips.shortcuts.add_dio"
      handleAdd={() => { } }
      allowColumnPicker={false}
      allowInc={false}
      disableControls={true} // do we need this, its very small table? todo: discuss
      showControlSwitch={false}
      onRowClick={handleRowSelect}
      onRowAction={(id: any) => { console.log(id); } }
      initialPageSize={25}
      allowRowAction={() => { return false; } }
      trackTableState={false}
      unselectRow={true}
      customRecordCount={records.length}
      gotoFirstPageOnSelect={true}
      deselectRowID={idToDeselect}
      setDeselectRowID={setIdToDeselect} onEdit={function (id: number): void {
        throw new Error("Function not implemented.");
      } }    />
  );
}

export default QualityControlDijeloviTable;
